import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useState } from 'react';

interface WizardNavigationProps {
  currentStep: number;
  totalSteps: number;
  canGoBack: boolean;
  canGoForward: boolean;
  isSubmitting: boolean;
  isLastStep: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function WizardNavigation({
  currentStep,
  totalSteps,
  canGoBack,
  canGoForward,
  isSubmitting,
  isLastStep,
  onBack,
  onNext,
  onSubmit,
  onCancel,
}: WizardNavigationProps) {
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  return (
    <div className="flex items-center justify-between border-t pt-6 mt-6">
      <div className="flex items-center gap-2">
        <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <DialogTrigger asChild>
            <Button variant="ghost" type="button">
              Cancel
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancel Migration Setup?</DialogTitle>
              <DialogDescription>
                Are you sure you want to cancel? Any entered data will be lost.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
                Continue Editing
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setShowCancelDialog(false);
                  onCancel();
                }}
              >
                Yes, Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <span className="text-sm text-muted-foreground">
          Step {currentStep} of {totalSteps}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          onClick={onBack}
          disabled={!canGoBack || isSubmitting}
          type="button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        {isLastStep ? (
          <Button
            onClick={onSubmit}
            disabled={!canGoForward || isSubmitting}
            type="button"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting Migration...
              </>
            ) : (
              'Start Migration'
            )}
          </Button>
        ) : (
          <Button
            onClick={onNext}
            disabled={!canGoForward || isSubmitting}
            type="button"
          >
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
